// One dialog at a time (a bottom sheet on phones, via the CSS). openModal(<Anything />) from anywhere; the content
// is a normal component, so it can load its own data. Escape or a click outside closes it.
import { Component, Suspense, useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import { ModalSkeleton } from "./loading";

type Entry = { node: ReactNode; onClose?: () => void; n: number };
let current: Entry | null = null;
let seq = 0;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

/** Show `node` in the dialog, replacing whatever was there. `onClose` runs when it is closed or replaced. */
export function openModal(node: ReactNode, onClose?: () => void) {
  const prev = current;
  current = { node, onClose, n: ++seq };
  emit();
  prev?.onClose?.();
}
export function closeModal() {
  const prev = current;
  current = null;
  emit();
  prev?.onClose?.();
}

export function ModalHost() {
  const m = useSyncExternalStore((cb) => { subs.add(cb); return () => subs.delete(cb); }, () => current);
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!m) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeModal(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [m]);
  useEffect(() => { // first field gets the keyboard, as before, unless the sheet leads with a suggestion (data-nofocus)
    if (m) requestAnimationFrame(() => {
      if (!card.current || card.current.querySelector("[data-nofocus]")) return;
      card.current.querySelector<HTMLElement>("input:not([type=hidden]),select,textarea")?.focus();
    });
  }, [m?.n]);
  return (
    <div id="modal" className={m ? "modal" : "modal hidden"} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
      <div className="modal-card" id="modal-card" ref={card}>
        {m && <ModalBoundary key={m.n}><Suspense fallback={<ModalSkeleton />}>{m.node}</Suspense></ModalBoundary>}
      </div>
    </div>
  );
}

class ModalBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return <><div className="banner red">{this.state.error.message}</div><div className="modal-actions"><button className="btn" onClick={closeModal}>Close</button></div></>;
  }
}
